import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  PhoneCall,
  Waves,
  Radio,
  Voicemail,
  Sparkles,
  ArrowDownRight,
  MoveRight,
} from "lucide-react";
import { LOGO_URL, COMPANY } from "@/lib/constants";
import { fadeUp, staggerParent, scaleIn } from "@/lib/motion";
import { CountUp, WordCycler, Marquee } from "@/components/motion-primitives";

const CAPABILITIES_TICKER = [
  "Hosted PBX",
  "Auto Attendant",
  "Smart Routing",
  "Voicemail-to-Email",
  "Hunt Groups",
  "SIP Trunking",
  "Call Recording",
  "SMS",
  "IVR",
  "E911",
  "Softphone Apps",
  "CRM Integrations",
];

const STATS = [
  { value: "99.99", suffix: "%", label: "Network uptime" },
  { value: "50", prefix: "<", suffix: "ms", label: "Call latency" },
  { value: "24/7", label: "US-based support" },
  { value: "500", suffix: "+", label: "Endpoints deployed" },
];

const LIVE_EVENTS = [
  { from: "Acme HQ", to: "Ext. 402", type: "Inbound" },
  { from: "Ext. 118", to: "+1 415-…", type: "Outbound" },
  { from: "Support IVR", to: "Queue A", type: "Routed" },
  { from: "Field Team", to: "Voicemail", type: "V-Mail" },
  { from: "Sales Line", to: "Ext. 205", type: "Inbound" },
  { from: "Ext. 077", to: "+1 512-…", type: "Outbound" },
];

const SERVICES_BENTO = [
  {
    span: "md:col-span-7 md:row-span-2",
    tint: "bg-ink text-white",
    kicker: "01",
    title: "Hosted PBX, engineered.",
    body: "Carrier-redundant infrastructure with regional failover, per-extension pricing, and rapid site rollouts.",
    icon: Waves,
    art: "logo",
  },
  {
    span: "md:col-span-5",
    tint: "bg-lime-pop text-ink",
    kicker: "02",
    title: "Smart call routing",
    body: "Time-of-day, skills-based, follow-me, and overflow — a router that thinks.",
    icon: Radio,
  },
  {
    span: "md:col-span-5",
    tint: "bg-coral text-ink",
    kicker: "03",
    title: "Voicemail-to-email",
    body: "Transcribed voicemails delivered to inbox in seconds. Shared team mailboxes included.",
    icon: Voicemail,
  },
  {
    span: "md:col-span-12",
    tint: "bg-white text-ink",
    kicker: "04",
    title: "Auto attendants that don't sound like a robot.",
    body: "Multi-level menus, business-hours schedules, dial-by-name — configured with your voice and brand.",
    icon: Sparkles,
  },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-28 sm:pt-40 pb-16 sm:pb-24 grain">
        <div className="absolute inset-x-0 top-0 h-[65vh] dotted opacity-40 pointer-events-none" />

        <div className="container-x relative">
          <motion.div
            variants={staggerParent}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end"
          >
            <div className="lg:col-span-8">
              <motion.div
                variants={fadeUp}
                className="inline-flex items-center gap-2 rounded-full bg-white border-ink border px-4 py-1.5 text-xs font-mono uppercase tracking-widest"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-coral animate-pulse" />
                Business communications, engineered
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="mt-6 font-display text-[13vw] sm:text-8xl lg:text-[8.5rem] font-medium leading-[0.9] tracking-[-0.045em] text-ink"
              >
                <span className="block">Phones that</span>
                <span className="block">
                  actually{" "}
                  <span className="inline-block">
                    <WordCycler
                      words={["answer.", "connect.", "route.", "scale.", "convert."]}
                      className="text-coral"
                    />
                  </span>
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-8 max-w-2xl text-lg sm:text-xl text-ink-2 leading-relaxed"
              >
                {COMPANY.name} designs, deploys, and manages PBX and unified
                communications for teams that live and die by the ring —
                inbound, outbound, IVRs, extensions, voicemail, and every
                route in between.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/contact"
                  className="btn-lime group text-base h-14 px-8"
                  data-testid="hero-cta-primary"
                >
                  Request an Inquiry
                  <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 h-14 px-6 rounded-full border border-ink text-ink hover:bg-ink hover:text-white transition-all font-medium text-base"
                  data-testid="hero-cta-secondary"
                >
                  Explore Services
                  <MoveRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </div>

            {/* Live calls ticker card */}
            <motion.div
              variants={scaleIn}
              className="lg:col-span-4 relative"
            >
              <div className="rounded-3xl bg-ink text-white p-6 shadow-[0_30px_60px_-30px_rgba(10,10,10,0.4)]">
                <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest">
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-lime-pop blink" />
                    Live network
                  </div>
                  <span className="text-white/50">last hour</span>
                </div>
                <div className="mt-5 font-display text-6xl tracking-tight">
                  <CountUp value="342" />
                </div>
                <div className="text-white/60 text-sm mt-1">calls routed</div>

                <div className="mt-6 space-y-2 max-h-[280px] overflow-hidden">
                  {LIVE_EVENTS.map((ev, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 + i * 0.09 }}
                      className="flex items-center justify-between text-[13px] font-mono border-t border-white/10 pt-2"
                    >
                      <span className="text-white/80 truncate">
                        {ev.from} → {ev.to}
                      </span>
                      <span
                        className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          ev.type === "Inbound"
                            ? "bg-lime-pop text-ink"
                            : ev.type === "Outbound"
                            ? "bg-coral text-ink"
                            : "bg-white/10 text-white/70"
                        }`}
                      >
                        {ev.type}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Floating logo tile — knockout filter strips white bg */}
              <motion.div
                initial={{ y: 20, opacity: 0, rotate: -6 }}
                animate={{ y: 0, opacity: 1, rotate: -6 }}
                transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="hidden lg:flex absolute -bottom-10 -left-14 w-36 h-36 items-center justify-center"
              >
                <img
                  src={LOGO_URL}
                  alt="Mavericks"
                  className="w-full h-full object-contain"
                  style={{ filter: "url(#knockout-white) drop-shadow(0 10px 20px rgba(0,0,0,0.25))" }}
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE TICKER */}
      <section className="border-y border-ink bg-bone-2 py-6">
        <Marquee>
          {CAPABILITIES_TICKER.map((c, i) => (
            <div
              key={i}
              className="flex items-center gap-6 font-display text-2xl sm:text-3xl text-ink"
            >
              <span>{c}</span>
              <span className="text-coral">✦</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* STATS */}
      <section className="py-20 sm:py-24">
        <div className="container-x">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerParent}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8"
          >
            {STATS.map((s) => (
              <motion.div
                key={s.label}
                variants={fadeUp}
                className="border-t-2 border-ink pt-5"
              >
                <div className="font-display text-4xl sm:text-6xl font-medium text-ink leading-none">
                  {s.prefix || ""}
                  {typeof s.value === "string" && s.value.includes("/") ? (
                    s.value
                  ) : (
                    <CountUp value={s.value} suffix={s.suffix || ""} />
                  )}
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-warm font-semibold">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-16 sm:py-24">
        <div className="container-x">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={staggerParent}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} className="text-overline">
              What we deliver
            </motion.div>
            <motion.h2
              variants={fadeUp}
              className="mt-4 font-display text-5xl sm:text-7xl text-ink leading-[0.95] tracking-tight"
            >
              A full comms stack. <br />
              <span className="italic text-muted-warm">Without the noise.</span>
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={staggerParent}
            className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[minmax(220px,auto)]"
          >
            {SERVICES_BENTO.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.35 }}
                  className={`${s.span} ${s.tint} rounded-3xl border border-ink p-7 sm:p-9 relative overflow-hidden group`}
                  data-testid={`service-card-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-xs uppercase tracking-widest opacity-70">
                      {s.kicker}
                    </span>
                    <Icon className="h-5 w-5" />
                  </div>

                  {s.art === "logo" && (
                    <img
                      src={LOGO_URL}
                      alt=""
                      aria-hidden="true"
                      className="absolute -right-24 -bottom-20 h-[26rem] w-[26rem] object-contain opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700 pointer-events-none"
                      style={{ filter: "url(#knockout-white)" }}
                    />
                  )}

                  <h3 className="mt-8 font-display text-2xl sm:text-4xl leading-[1.02] tracking-tight max-w-md">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base max-w-md opacity-80 leading-relaxed">
                    {s.body}
                  </p>
                  <ArrowDownRight className="absolute bottom-6 right-6 h-6 w-6 opacity-70 group-hover:opacity-100 group-hover:-rotate-45 transition-all" />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 sm:py-32 bg-bone-2 border-y border-ink">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="text-overline">Process</div>
            <h2 className="mt-4 font-display text-5xl sm:text-6xl text-ink leading-[0.95] tracking-tight">
              Discovery to dial-tone in three moves.
            </h2>
            <p className="mt-5 text-ink-2 leading-relaxed max-w-md">
              Every deployment starts with a technical assessment and ends
              with hands-on training. We don't hand off and disappear.
            </p>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {[
              {
                step: "01",
                title: "Assess",
                desc: "Map current call flows, extensions, and pain points across every site.",
              },
              {
                step: "02",
                title: "Design & deploy",
                desc: "Engineer a hosted or on-prem PBX, port numbers, configure routing, train users.",
              },
              {
                step: "03",
                title: "Manage",
                desc: "Proactive monitoring, moves/adds/changes, 24/7 incident response.",
              },
            ].map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                whileHover={{ x: 10 }}
                className="group flex items-start gap-6 p-7 rounded-3xl border border-ink bg-bone hover:bg-white transition-colors"
              >
                <div className="font-mono text-coral text-sm pt-2">{s.step}</div>
                <div className="flex-1">
                  <h3 className="font-display text-2xl sm:text-3xl text-ink">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-ink-2 leading-relaxed">{s.desc}</p>
                </div>
                <ArrowUpRight className="h-6 w-6 text-ink transition-transform group-hover:rotate-45" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-32">
        <div className="container-x">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="relative rounded-[2rem] overflow-hidden bg-ink text-white p-10 sm:p-20 grain"
          >
            <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-lime-pop blur-3xl opacity-25 pointer-events-none" />
            <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-coral blur-3xl opacity-20 pointer-events-none" />

            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.22em] text-lime-pop mb-4">
                  Get started
                </div>
                <h2 className="font-display text-5xl sm:text-6xl tracking-tight leading-[0.98]">
                  Ready to upgrade your business phone system?
                </h2>
                <p className="mt-6 text-white/70 text-lg max-w-lg leading-relaxed">
                  Tell us about your team. We'll respond within one business
                  day with a tailored quote and architecture.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/contact"
                  className="btn-lime group text-base h-14 px-8"
                  data-testid="footer-cta-inquire"
                >
                  Start an Inquiry
                  <ArrowUpRight className="h-5 w-5 transition-transform group-hover:rotate-45" />
                </Link>
              </div>
            </div>

            <div className="relative mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-white/10 pt-8">
              <div>
                <div className="text-lime-pop text-xs uppercase tracking-widest font-mono">
                  Call
                </div>
                <a
                  href={`tel:+${COMPANY.phoneRaw}`}
                  className="mt-1 block font-display text-xl hover:text-lime-pop"
                >
                  {COMPANY.phone}
                </a>
              </div>
              <div>
                <div className="text-lime-pop text-xs uppercase tracking-widest font-mono">
                  Email
                </div>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="mt-1 block font-display text-lg hover:text-lime-pop break-all"
                >
                  {COMPANY.email}
                </a>
              </div>
              <div>
                <div className="text-lime-pop text-xs uppercase tracking-widest font-mono">
                  Coverage
                </div>
                <div className="mt-1 font-display text-lg">United States</div>
              </div>
              <div>
                <div className="text-lime-pop text-xs uppercase tracking-widest font-mono">
                  Response
                </div>
                <div className="mt-1 font-display text-lg">≤ 1 business day</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
